import { create } from 'zustand';
import type { QuoteRowStatus } from '@/types/product';

/**
 * Nieuwste productversies voor de offerte die open staat.
 *
 * Gevuld vanuit useQuoteProductStatus. De editor vergelijkt hiermee het
 * productVersionId op de tabelrij zelf, niet de rij-id's uit de blokken: zo
 * wisselt een regel direct van status zodra hij is bijgewerkt of genegeerd,
 * zonder te wachten op de volgende synchronisatie.
 */
export interface LatestProductVersion {
  productName: string;
  versionId: string;
  version: number;
}

export interface OutdatedRowVersion extends LatestProductVersion {
  /** Versienummer waar de regel nu op staat; null als dat (nog) niet bekend is. */
  usedVersion: number | null;
}

interface ProductVersionState {
  latest: Record<string, LatestProductVersion>;
  /** Versienummer per versie-id, voor "v1 → v2". */
  versionNumbers: Record<string, number>;
  setStatuses: (statuses: QuoteRowStatus[]) => void;
  reset: () => void;
}

function sameRecord<T>(a: Record<string, T>, b: Record<string, T>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const useProductVersionStore = create<ProductVersionState>()((set) => ({
  latest: {},
  versionNumbers: {},
  setStatuses: (statuses) =>
    set((state) => {
      const latest: Record<string, LatestProductVersion> = {};
      const versionNumbers: Record<string, number> = {};
      for (const status of statuses) {
        latest[status.productId] = {
          productName: status.productName,
          versionId: status.latestVersionId,
          version: status.latestVersion,
        };
        versionNumbers[status.usedVersionId] = status.usedVersion;
        versionNumbers[status.latestVersionId] = status.latestVersion;
      }
      if (sameRecord(state.latest, latest) && sameRecord(state.versionNumbers, versionNumbers)) {
        return state;
      }
      return { latest, versionNumbers };
    }),
  reset: () => set({ latest: {}, versionNumbers: {} }),
}));

/** Geeft de nieuwere versie terug als deze regel erachter loopt, anders null. */
export function outdatedRowVersion(
  state: Pick<ProductVersionState, 'latest' | 'versionNumbers'>,
  productId: string | null | undefined,
  productVersionId: string | null | undefined
): OutdatedRowVersion | null {
  if (!productId || !productVersionId) return null;
  const latest = state.latest[productId];
  if (!latest || latest.versionId === productVersionId) return null;
  return { ...latest, usedVersion: state.versionNumbers[productVersionId] ?? null };
}
